import { useState } from 'react'
import { formatAed, formatMoney } from '../../lib/money'
import { back } from '../../lib/router'
import { useStore } from '../../store'
import { Card, Chip, Money, TopBar } from '../../ui/parts'
import { monthLabel } from '../engine'
import { deriveBudget, deriveLog } from '../repo'
import type { LedgerType, LogRow } from '../types'

type Filter = 'all' | 'spending' | 'incoming'

const LABELS: Record<LedgerType, string> = {
  ROLLOVER: 'Rollover',
  BUDGET_ADDED: 'Monthly budget',
  ADJUSTMENT: 'Adjustment',
  EARNED: 'Earned',
  REFUND: 'Refund',
  PURCHASE_FULL: 'Purchase',
  PURCHASE_SPLIT: 'Purchase (split)',
  INSTALLMENT_DUE: 'Installment',
  PURCHASE_CANCELLED: 'Cancelled',
}

const TONES: Record<LedgerType, '' | 'mint' | 'coral' | 'amber' | 'indigo' | 'lilac'> = {
  ROLLOVER: '',
  BUDGET_ADDED: 'indigo',
  ADJUSTMENT: 'lilac',
  EARNED: 'mint',
  REFUND: 'mint',
  PURCHASE_FULL: 'coral',
  PURCHASE_SPLIT: 'coral',
  INSTALLMENT_DUE: 'coral',
  PURCHASE_CANCELLED: 'amber',
}

export function LogScreen() {
  const { snapshot } = useStore()
  const [filter, setFilter] = useState<Filter>('all')

  if (!snapshot) return null

  const budget = deriveBudget(snapshot)
  const rows = deriveLog(snapshot).filter((row) => {
    if (filter === 'all') return true
    if (filter === 'spending') return row.amountFils < 0 || row.type === 'PURCHASE_CANCELLED'
    return (
      row.type === 'BUDGET_ADDED' ||
      row.type === 'EARNED' ||
      row.type === 'REFUND' ||
      (row.type === 'ADJUSTMENT' && row.amountFils > 0)
    )
  })

  const months = [...new Set(rows.map((r) => r.month))].sort().reverse()

  return (
    <div className="screen">
      <TopBar title="Activity log" onBack={back} />

      <div className="stack">
        {budget.committed.length > 0 ? (
          <Card>
            <div style={{ fontWeight: 650 }}>Committed to future months</div>
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
              {formatAed(budget.committedFutureFils)} of installments already spoken for. These are
              not spent yet, so they do not appear in the log below.
            </div>
            <div style={{ marginTop: 10 }}>
              {budget.committed.map((entry) => (
                <div className="row between" key={`${entry.month}-${entry.itemTitle}-${entry.seq}`}>
                  <span style={{ fontSize: 13 }}>
                    {entry.itemTitle} ({entry.seq}/{entry.totalCount})
                  </span>
                  <span className="tnum" style={{ fontSize: 13 }}>
                    {monthLabel(entry.month)} · {formatMoney(entry.amountFils)}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        <div className="filters">
          {(['all', 'spending', 'incoming'] as Filter[]).map((option) => (
            <button
              key={option}
              type="button"
              className={`filter ${filter === option ? 'on' : ''}`}
              onClick={() => setFilter(option)}
            >
              {option === 'all' ? 'Everything' : option === 'spending' ? 'Spending' : 'Budget added'}
            </button>
          ))}
        </div>

        {months.length === 0 ? <div className="muted">Nothing logged yet.</div> : null}

        {months.map((month) => {
          const monthRows = rows.filter((r) => r.month === month)
          const opening = monthRows.find((r) => r.type === 'ROLLOVER')?.amountFils ?? 0
          const closing = monthRows.at(-1)?.runningBalanceFils ?? 0
          return (
            <div key={month}>
              <div className="month-head">
                <div className="m">{monthLabel(month)}</div>
                <div className="label tnum">
                  Rolled in {formatMoney(opening)} · closed at {formatMoney(closing)}
                </div>
              </div>
              <div className="stack">
                {[...monthRows].reverse().map((row) => (
                  <LogLine key={row.id} row={row} />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function LogLine({ row }: { row: LogRow }) {
  return (
    <Card tight>
      <div className="row">
        <div className="grow">
          <div className="log-note">{row.note}</div>
          <div className="row" style={{ marginTop: 4 }}>
            <Chip tone={TONES[row.type]}>{LABELS[row.type]}</Chip>
            {row.timestamp ? (
              <span className="label">
                {new Date(row.timestamp).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            ) : null}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="log-amount">
            <Money fils={row.amountFils} signed />
          </div>
          {row.affectsBalance ? (
            <div className="log-balance">{formatMoney(row.runningBalanceFils)}</div>
          ) : null}
        </div>
      </div>
    </Card>
  )
}
